<script lang="ts" setup>
	import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
	import { ref, watch } from 'vue'
	import { useRoute, useRouter } from 'vue-router'

	import BreadCrumb, { Page } from '@/components/BreadCrumb.vue'
	import LoadingModal from '@/components/LoadingModal.vue'

	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useHotels from '../../../composables/useHotels'
	import AgreementTab from './partials/AgreementTab.vue'
	import AgreementsTab from './partials/AgreementsTab.vue'
	import BanksAccounts from './partials/BanksAccounts.vue'
	import CommentsTab from './partials/CommentsTab.vue'
	import ContactsTab from './partials/ContactsTab.vue'
	import CreditTab from './partials/CreditTab.vue'
	import FiscalTab from './partials/FiscalTab.vue'
	import GeneralnformationTab from './partials/GeneralnformationTab.vue'
	import LocationTab from './partials/LocationTab.vue'
	import LogTab from './partials/LogTab.vue'
	import RatePlaneCodesTab from './partials/RatePlaneCodesTab.vue'

	const route = useRoute()

	const { hasPermissionUser, abilities, redirectTo404 } = useHasPermissionUser()

	const { data, isLoading } = useHotels({
		immediate: true,
		params: {
			id: Number(route.params.id),
		},
	})

	const tabs = ref([
		{
			name: 'Información general',
			component: GeneralnformationTab,
			permission: ['update-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Ubicación y Fachada',
			component: LocationTab,
			permission: ['update-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Datos fiscales',
			component: FiscalTab,
			permission: ['view-billingInformation-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			component: AgreementsTab,
			name: 'Tarifas',
			permission: ['update-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			component: RatePlaneCodesTab,
			name: 'Códigos de tarifas',
			permission: ['update-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Cuentas bancarias',
			component: BanksAccounts,
			permission: ['view-bankAccounts-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Contactos',
			component: ContactsTab,
			permission: ['view-contacts-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Crédito',
			component: CreditTab,
			permission: ['view-credit-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Convenio',
			component: AgreementTab,
			permission: ['viewAny-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Comentarios',
			component: CommentsTab,
			permission: ['viewAny-hotel-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
		{
			name: 'Historial de cambios',
			component: LogTab,
			permission: ['view-logs-internal'],
			entityType: ['App\\Models\\Hotel'],
		},
	])

	const pages = ref<Page[]>([])

	watch(
		data,
		(value) => {
			if (value) {
				pages.value = [
					{
						name: 'Proveedores de hoteleria',
						to: {
							name: 'hotel',
						},
					},
					{ name: 'Hoteles', to: { name: 'hotel.hotels' }, current: false },
					{
						name: `Editar ${value.name}`,
						to: { name: 'hotels.hotel.edit', params: { id: value.id } },
						current: true,
					},
				]
			}
		},
		{
			immediate: true,
		}
	)
</script>

<template>
	<BreadCrumb :pages="pages" />

	<TabGroup
		as="div"
		class="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto px-4 lg:px-[2.75rem] py-2 mb-7"
		v-if="!isLoading && data"
	>
		<TabList
			as="aside"
			class="flex flex-col gap-4 lg:w-1/3 lg:max-w-[13.75rem] !lg:sticky mt-8"
		>
			<nav
				v-dragscroll.x
				class="no-scrollbar flex touch-pan-x px-2 lg:p-0 w-full lg:max-w-[13.75rem] flex-row lg:flex-col gap-2 overflow-x-auto"
			>
				<Tab
					v-for="(tab, i) in tabs.filter((subItem) =>
						subItem.permission.some((ability, i) => {
							if (subItem.name === 'Tarifas') {
								return !Boolean(data?.amadeusCode)
							}

							return hasPermissionUser(ability, subItem.entityType[i])
						})
					)"
					:key="i"
					v-slot="{ selected }"
					class="rounded-md w-[13.75rem]"
				>
					<button
						:class="[
							'w-full py-3 px-2 font-medium whitespace-nowrap rounded-md text-white focus:outline-none select-none',
							selected ? 'bg-[#253C70]' : 'bg-[#1F2937]',
						]"
					>
						{{ tab.name }}
					</button>
				</Tab>
			</nav>
		</TabList>

		<TabPanels
			as="div"
			class="w-full"
		>
			<h3 class="text-2xl mb-2 font-bold capitalize font-inter">
				{{ data?.name }}
			</h3>
			<TabPanel
				v-for="(tab, i) in tabs.filter((subItem) =>
					subItem.permission.some((ability, i) => {
						if (subItem.name === 'Tarifas') {
							return !Boolean(data?.amadeusCode)
						}

						return hasPermissionUser(ability, subItem.entityType[i])
					})
				)"
				:key="i"
			>
				<component
					:is="tab.component"
					:hotel="data!"
				/>
			</TabPanel>
		</TabPanels>
	</TabGroup>

	<LoadingModal :when="isLoading" />
</template>
